import type { Directive, DirectiveBinding } from 'vue'

type TooltipPosition = 'top' | 'right' | 'bottom' | 'left'
type TooltipTheme = 'primary' | 'success' | 'warning' | 'danger' | 'secondary'

interface TooltipOptions {
  value: string
  modifiers: {
    [key in TooltipPosition | TooltipTheme | 'html' | 'disabled' | 'noMobile']?: boolean
  }
  arg?: string
}

interface TooltipConfig {
  defaultDelay: number
  touchDelay: number
  hideDelay: number
  defaultPosition: TooltipPosition
  defaultText: string
}

interface TooltipEventHandlers {
  mouseenter: (e: MouseEvent) => void
  mouseleave: (e: MouseEvent) => void
  focus: (e: FocusEvent) => void
  blur: (e: FocusEvent) => void
  touchstart: (e: TouchEvent) => void
  touchend: (e: TouchEvent) => void
}

interface TooltipElement extends HTMLElement {
  disabled?: boolean
}

const config: TooltipConfig = {
  defaultDelay: 200,
  touchDelay: 700,
  hideDelay: 100,
  defaultPosition: 'top',
  defaultText: ''
}

const getPosition = (modifiers: TooltipOptions['modifiers']): TooltipPosition => {
  const positions: TooltipPosition[] = ['top', 'right', 'bottom', 'left']
  return positions.find((pos) => modifiers[pos]) || config.defaultPosition
}

const isTouchDevice = (): boolean => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0
}

const setupTooltip = (el: TooltipElement, binding: DirectiveBinding<string>) => {
  const tooltip: HTMLSpanElement = document.createElement('span')
  const classes = ['custom-tooltip']
  let showTimeout: ReturnType<typeof setTimeout>
  let hideTimeout: ReturnType<typeof setTimeout>

  // Set up position class or default
  const position = getPosition(binding.modifiers)
  classes.push(`tooltip-${position}`)

  // Add theme classes or default
  const themes: TooltipTheme[] = ['primary', 'success', 'warning', 'danger', 'secondary']
  let foundTheme = false

  for (const theme of themes) {
    if (binding.modifiers[theme]) {
      classes.push(`tooltip-${theme}`)
      foundTheme = true
      break
    }
  }

  if (!foundTheme) {
    classes.push('tooltip-default')
  }
  tooltip.className = classes.join(' ')

  const setContent = () => {
    if (binding.modifiers.html) {
      tooltip.innerHTML = binding.value || config.defaultText
    } else {
      tooltip.textContent = binding.value || config.defaultText
    }
  }
  setContent()

  // Add ARIA attributes
  const tooltipId = `tooltip-${Math.random().toString(36).substr(2, 9)}`
  el.setAttribute('aria-describedby', tooltipId)
  tooltip.id = tooltipId
  tooltip.setAttribute('role', 'tooltip')

  document.body.appendChild(tooltip)

  const updatePosition = () => {
    const rect = el.getBoundingClientRect()
    const tooltipRect = tooltip.getBoundingClientRect()
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop

    let top = 0
    let left = 0

    switch (position) {
      case 'top':
        top = rect.top + scrollTop - tooltipRect.height - 8
        left = rect.left + scrollLeft + (rect.width - tooltipRect.width) / 2
        break
      case 'bottom':
        top = rect.bottom + scrollTop + 10
        left = rect.left + scrollLeft + rect.width / 2 - tooltipRect.width / 2
        break
      case 'left':
        top = rect.top + scrollTop + (rect.height - tooltipRect.height) / 2
        left = rect.left + scrollLeft - tooltipRect.width - 8
        break
      case 'right':
        top = rect.top + scrollTop + (rect.height - tooltipRect.height) / 2
        left = rect.right + scrollLeft + 8
        break
    }

    // Ensure tooltip stays within viewport
    const viewportWidth = window.innerWidth
    if (left < 0) left = 0
    if (left + tooltipRect.width > viewportWidth) {
      left = viewportWidth - tooltipRect.width
    }

    tooltip.style.position = 'absolute'
    tooltip.style.top = `${Math.round(top)}px`
    tooltip.style.left = `${Math.round(left)}px`
  }

  // Handle showing tooltip
  const showTooltip = (immediate = false): void => {
    if (el.disabled || binding.modifiers.disabled) return
    if (isTouchDevice() && binding.modifiers.noMobile) return

    clearTimeout(hideTimeout)
    clearTimeout(showTimeout)

    const delay = immediate
      ? 0
      : Number(binding.arg) || (isTouchDevice() ? config.touchDelay : config.defaultDelay)

    showTimeout = setTimeout(() => {
      setContent()
      updatePosition()
      tooltip.style.visibility = 'visible'
      tooltip.style.opacity = '1'
      el.dispatchEvent(new CustomEvent('tooltip:show'))
    }, delay)
  }

  // Handle hiding tooltip
  const hideTooltip = (immediate = false): void => {
    clearTimeout(showTimeout)
    clearTimeout(hideTimeout)

    const delay = immediate ? 0 : config.hideDelay

    hideTimeout = setTimeout(() => {
      tooltip.style.visibility = 'hidden'
      tooltip.style.opacity = '0'
      el.dispatchEvent(new CustomEvent('tooltip:hide'))
    }, delay)
  }

  // Add scroll and resize listeners to update position
  const handleScrollResize = () => {
    if (tooltip.style.visibility === 'visible') {
      updatePosition()
    }
  }

  window.addEventListener('scroll', handleScrollResize, true)
  window.addEventListener('resize', handleScrollResize)

  // Store handlers for cleanup
  ;(el as any)._tooltipHandlers = {
    scroll: handleScrollResize,
    resize: handleScrollResize
  }

  // Define event handlers with proper types
  const events: TooltipEventHandlers = {
    mouseenter: (e: MouseEvent) => showTooltip(),
    mouseleave: (e: MouseEvent) => hideTooltip(),
    focus: (e: FocusEvent) => showTooltip(true),
    blur: (e: FocusEvent) => hideTooltip(true),
    touchstart: (e: TouchEvent) => {
      e.preventDefault()
      showTooltip()
    },
    touchend: (e: TouchEvent) => hideTooltip()
  }

  // Add listeners with proper types
  Object.entries(events).forEach(([event, handler]) => {
    el.addEventListener(event as keyof HTMLElementEventMap, handler as EventListener)
  })

  // Store event handlers for cleanup
  ;(el as any)._tooltipListeners = events
  ;(el as any)._tooltip = tooltip
}

const destroyTooltip = (el: TooltipElement): void => {
  // Remove scroll and resize listeners
  const handlers = (el as any)._tooltipHandlers
  if (handlers) {
    window.removeEventListener('scroll', handlers.scroll, true)
    window.removeEventListener('resize', handlers.resize)
    delete (el as any)._tooltipHandlers
  }

  // Remove event listeners
  const listeners = (el as any)._tooltipListeners as TooltipEventHandlers
  if (listeners) {
    Object.entries(listeners).forEach(([event, handler]) => {
      el.removeEventListener(event as keyof HTMLElementEventMap, handler as EventListener)
    })
    delete (el as any)._tooltipListeners
  }

  // Remove tooltip from body
  const tooltip = (el as any)._tooltip
  if (tooltip && tooltip.parentNode) {
    tooltip.parentNode.removeChild(tooltip)
  }
  delete (el as any)._tooltip
}

const vTooltip: Directive<TooltipElement, string> = {
  mounted(el: TooltipElement, binding: DirectiveBinding<string>): void {
    // Prevent tooltip from mounting if value is empty
    if (!binding.value) return
    setupTooltip(el, binding)
  },
  updated(el: TooltipElement, binding: DirectiveBinding<string>): void {
    const tooltip = (el as any)._tooltip

    if (tooltip instanceof HTMLElement) {
      if (!binding.value) {
        destroyTooltip(el)
      }
      if (binding.modifiers.html) {
        tooltip.innerHTML = binding.value || config.defaultText
      } else {
        tooltip.textContent = binding.value || config.defaultText
      }

      if (el.disabled || binding.modifiers.disabled) {
        tooltip.style.visibility = 'hidden'
        tooltip.style.opacity = '0'
      }
    } else if (binding.value) {
      setupTooltip(el, binding)
    }
  },

  unmounted(el: TooltipElement): void {
    const tooltip = (el as any)._tooltip

    if (tooltip instanceof HTMLElement) {
      destroyTooltip(el)
    }
  }
}

export default vTooltip
